// Lager 2.1 — delad refund/cancel-dialog (D9-villkor 1 på UI-lagret).
//
// Mounted av BÅDE:
//   - BookingDetailActions (ersätter AdminCancelBookingDialog, Decision 10.3)
//   - BookingDetailPanel (D2-knappar i shell-footer, ny entrypoint)
//   - (framtida 2.3) ActivityDetailModal för canonical open-match
//
// kind-prop styr vilka modes som är tillåtna:
//   - 'private': full_override + cancel_no_refund (2.1 active)
//   - 'open_match_legacy': samma som private (legacy = inga per-deltagar-charges)
//   - 'open_match_canonical': throw early i 2.1 (Lager 2.3-territorium)
//
// Decision 10.4: "Återbetala enligt regler"-knapp = disabled-med-tooltip
// (NOT full-refund-fallback). Aktiveras i Lager 2.2 när per_rules-mode
// byggs.
//
// Decision 10.6: behåll sendTemplateEmail('booking_cancellation') efter
// success (befintligt mönster — kund får email-bekräftelse).

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
// Lager 2.2: Tooltip-imports borttagna — per_rules-radio är nu aktiv
// (var disabled-med-tooltip i 2.1). Dynamiskt label + sub-text ersätter
// tooltip-pattern per Decision 10.4 (transparens innan submit).
import { AlertTriangle, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { sendTemplateEmail } from "@/lib/sendTemplateEmail";
import {
  resolveRefundCancelPlan,
  executeRefundCancelPlan,
  type RefundablePayment,
  type RefundMode,
} from "@/lib/refundCancelPlan";
import {
  resolveTierRefundFromBooking,
  type CancellationTier,
  type TierRefundResult,
} from "@/lib/cancellationFee";
import type { Tables } from "@/integrations/supabase/types";

export type BookingRefundCancelKind =
  | "private"
  | "open_match_legacy"
  | "open_match_canonical"; // 2.3 — throws i dialogen om passas i 2.1

interface BookingRefundCancelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: BookingRefundCancelKind;
  /** Förvald mode när dialogen öppnas (om null → user väljer själv).
   *  Lager 2.2: lägger till 'per_rules' som aktivt val (var disabled i 2.1). */
  initialMode?: Extract<RefundMode, "full_override" | "cancel_no_refund" | "per_rules"> | null;
  booking: {
    id: string;
    start_time: string;
    end_time: string;
    courts?: { name?: string | null } | null;
    profiles?: { full_name?: string | null } | null;
    creator?: { full_name?: string | null } | null;
  };
  payments: Tables<"booking_payments">[];
  bookerEmail?: string | null;
  /** Antal spelare för UI-summary (valfritt) */
  playerCount?: number;
  /** Callback efter lyckad cancel — for state-reset / panel-close */
  onCancelled: () => void;
}

export function BookingRefundCancelDialog({
  open,
  onOpenChange,
  kind,
  initialMode = null,
  booking,
  payments,
  bookerEmail,
  playerCount,
  onCancelled,
}: BookingRefundCancelDialogProps) {
  const { t } = useTranslation("admin");
  const { user } = useAuth();
  const { organization } = useOrganization();
  const qc = useQueryClient();

  // ── Härleda payment-state ─────────────────────────────────────────────
  const paidPayments = useMemo(
    () => payments.filter((p) => ["paid", "manual"].includes(p.status)),
    [payments],
  );
  const paidAmountMinor = useMemo(
    () =>
      paidPayments.reduce(
        (s, p) =>
          s +
          (typeof p.amount_minor === "number"
            ? p.amount_minor
            : Math.round((p.amount ?? 0) * 100)),
        0,
      ),
    [paidPayments],
  );
  const paidAmount = paidAmountMinor / 100;
  const hasStripePayment = paidPayments.some((p) => p.stripe_payment_intent_id);
  const hasPaidPayments = paidPayments.length > 0;

  // ── Mode-val (radio inside dialog för regression-coverage) ────────────
  // Lager 2.2: 'per_rules' är nu giltigt mode-val (aktivt enligt org.tiers).
  // Default-mode: använd initialMode om angivet, annars full_override för
  // betalda bokningar, cancel_no_refund för gratis.
  const [mode, setMode] = useState<"full_override" | "cancel_no_refund" | "per_rules">(
    initialMode ?? "cancel_no_refund",
  );
  const [reason, setReason] = useState("");

  // BUG-FIX (audit 2026-05-19, Lesson 28): BookingRefundCancelDialog mountades
  // med open=false från BookingDetailPanel (conditional rendering på `booking`
  // men inte på `open`). useState kördes en gång vid mount med
  // initialMode=null + hasPaidPayments=false (payments ej laddat ännu)
  // → mode låstes till cancel_no_refund. Fix: useEffect re-synkar mode
  // varje gång dialogen öppnas eller initialMode/hasPaidPayments ändras.
  useEffect(() => {
    if (open) {
      setMode(initialMode ?? (hasPaidPayments ? "full_override" : "cancel_no_refund"));
      setReason("");
    }
  }, [open, initialMode, hasPaidPayments]);

  // ── Lager 2.2: P1-tier-resolver (Alt A — org.settings.cancellation.tiers) ─
  // Tiers fetchas från useOrganization (Decision 10.7 — samma källa som
  // useMyBookings.ts:192). Demo har tiers=null → resolveTierRefundFromBooking
  // returnerar refundPercent=100 + tierLabel "inga regler konfigurerade"
  // (Decision 10.2 — knappen aktiv även för demo, full refund som default).
  const cancellationSettings = (organization?.settings?.cancellation ?? {}) as {
    tiers?: CancellationTier[];
  };
  const orgTiers = useMemo(
    () => cancellationSettings.tiers ?? [],
    [cancellationSettings.tiers],
  );

  const tierResult: TierRefundResult | null = useMemo(() => {
    if (!booking?.start_time) return null;
    return resolveTierRefundFromBooking({
      startTime: new Date(booking.start_time),
      tiers: orgTiers,
    });
  }, [booking?.start_time, orgTiers]);

  // Expected refund-amount enligt regel (för UI-transparens, Decision 10.4)
  const perRulesRefundMinor = useMemo(() => {
    if (!tierResult) return 0;
    return Math.round((paidAmountMinor * tierResult.refundPercent) / 100);
  }, [tierResult, paidAmountMinor]);
  const perRulesRefundKr = perRulesRefundMinor / 100;

  const handleOpenChange = (v: boolean) => {
    // Reset hanteras av useEffect ovan när open går false→true.
    onOpenChange(v);
  };

  // ── Mutation ──────────────────────────────────────────────────────────
  const cancelMutation = useMutation({
    mutationFn: async () => {
      // Decision 10.1: open_match_canonical kastar i resolvern → fångar här
      if (kind === "open_match_canonical") {
        throw new Error(
          t("refundCancel.openMatchCanonicalUnsupported", {
            defaultValue:
              "Open-match-avbokning är inte byggd än (kommer i Lager 2.3).",
          }),
        );
      }

      // Mappa booking_payments → RefundablePayment-shape
      const refundablePayments: RefundablePayment[] = payments.map((p) => ({
        paymentId: p.id,
        status: p.status,
        amountMinor:
          typeof p.amount_minor === "number"
            ? p.amount_minor
            : Math.round((p.amount ?? 0) * 100),
        stripePaymentIntentId: p.stripe_payment_intent_id ?? null,
        userId: p.user_id ?? "",
      }));

      // Lager 2.2: pass rulesRefundPercent + perRulesMeta för per_rules-mode.
      // Resolvern hanterar auto-degrade (0% → cancel_no_refund, 100% →
      // full_override) + öre-exakt partial refund i [0, 100].
      const plan = resolveRefundCancelPlan({
        bookingId: booking.id,
        payments: refundablePayments,
        mode,
        ...(mode === "per_rules" && tierResult
          ? {
              rulesRefundPercent: tierResult.refundPercent,
              perRulesMeta: {
                matchedTier: tierResult.matchedTier,
                refundPercent: tierResult.refundPercent,
                hoursLeft: tierResult.hoursLeft,
                tierLabel: tierResult.tierLabel,
              },
            }
          : {}),
      });

      const result = await executeRefundCancelPlan(plan, {
        supabase,
        adminUserId: user?.id ?? "",
        reason,
      });

      // Email (Decision 10.6 — behåll befintligt mönster). Fire-and-forget,
      // failure logas inte som critical (samma som AdminCancelBookingDialog).
      if (result.success && bookerEmail && organization?.id) {
        const start = new Date(booking.start_time);
        const end = new Date(booking.end_time);
        sendTemplateEmail(organization.id, "booking_cancellation", bookerEmail, {
          playerName: booking.profiles?.full_name ?? "Spelare",
          courtName: booking.courts?.name ?? "Bana",
          date: format(start, "d MMMM yyyy", { locale: sv }),
          startTime: format(start, "HH:mm"),
          endTime: format(end, "HH:mm"),
          clubName: organization.name,
        }).catch((err) => console.error("[refund-cancel] email failed:", err));
      }

      return result;
    },
    onSuccess: (result) => {
      // Invalidera samma query-keys som AdminCancelBookingDialog gjorde
      // (regression-coverage 10.3)
      qc.invalidateQueries({ queryKey: ["booking-detail"] });
      qc.invalidateQueries({ queryKey: ["booking-payments-detail"] });
      qc.invalidateQueries({ queryKey: ["booking-activity-log"] });
      qc.invalidateQueries({ queryKey: ["schedule-bookings"] });
      qc.invalidateQueries({ queryKey: ["admin-all-bookings"] });

      if (!result.success) {
        // Halv-state — refund kanske lyckades men cancel failade
        toast.error(
          result.halfStateWarning ??
            result.cancelError ??
            t("refundCancel.cancelFailedGeneric", {
              defaultValue: "Kunde inte avboka — försök igen",
            }),
        );
        return;
      }

      if (result.failedRefunds.length > 0) {
        // Cancel OK men någon refund failade
        toast.warning(
          t("refundCancel.cancelOkRefundFailed", {
            defaultValue:
              "Bokningen avbokades men {{count}} återbetalning(ar) misslyckades — kolla logg",
            count: result.failedRefunds.length,
          }),
        );
      } else if (result.refundedCount > 0) {
        toast.success(
          t("refundCancel.cancelOkRefunded", {
            defaultValue: "Avbokad + återbetalning {{amount}} kr",
            amount: Math.round(result.refundedAmountMinor / 100),
          }),
        );
      } else {
        toast.success(
          t("refundCancel.cancelOk", {
            defaultValue: "Bokningen avbokad",
          }),
        );
      }

      onOpenChange(false);
      onCancelled();
    },
    onError: (err: Error) => {
      toast.error(err.message || t("refundCancel.cancelFailedGeneric", {
        defaultValue: "Kunde inte avboka",
      }));
    },
  });

  const start = new Date(booking.start_time);
  const end = new Date(booking.end_time);

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("refundCancel.title", { defaultValue: "Avboka bokning" })}
          </AlertDialogTitle>
        </AlertDialogHeader>

        <div className="space-y-4">
          {/* Booking summary */}
          <div className="rounded-lg border p-3 space-y-1 text-sm">
            <p className="font-medium">
              {booking.courts?.name ?? "Bana"} &middot;{" "}
              {format(start, "EEE d MMM", { locale: sv })} &middot;{" "}
              {format(start, "HH:mm")}–{format(end, "HH:mm")}
            </p>
            <p className="text-muted-foreground">
              {t("refundCancel.bookedBy", { defaultValue: "Bokad av" })}:{" "}
              {booking.creator?.full_name ??
                booking.profiles?.full_name ??
                bookerEmail ??
                "—"}
            </p>
            {typeof playerCount === "number" && (
              <p className="text-muted-foreground">
                {playerCount}{" "}
                {playerCount === 1
                  ? t("refundCancel.player", { defaultValue: "spelare" })
                  : t("refundCancel.players", { defaultValue: "spelare" })}
              </p>
            )}
          </div>

          {/* Payment info + refund mode-selection */}
          {hasPaidPayments ? (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/30 p-3 space-y-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-600 shrink-0" />
                <span className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                  {t("refundCancel.paidAmount", { defaultValue: "Betalt" })}:{" "}
                  {paidAmount} kr
                  {hasStripePayment && (
                    <Badge variant="outline" className="ml-2 text-[10px]">
                      Stripe
                    </Badge>
                  )}
                </span>
              </div>

              <RadioGroup
                value={mode}
                onValueChange={(v) =>
                  setMode(v as "full_override" | "cancel_no_refund" | "per_rules")
                }
                className="gap-2"
              >
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value="full_override" className="mt-0.5" />
                  <span>
                    {t("refundCancel.optionFullRefund", {
                      defaultValue: "Avboka + full återbetalning",
                    })}{" "}
                    ({paidAmount} kr)
                  </span>
                </label>

                {/* Lager 2.2: per_rules-radio AKTIVERAD (var disabled i 2.1).
                    Decision 10.4: UI-transparens med dynamiskt label visar
                    exakt utfall innan submit (belopp + tier-label). */}
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value="per_rules" className="mt-0.5" />
                  <div className="flex-1">
                    <span>
                      {t("refundCancel.optionPerRules", {
                        defaultValue: "Avboka + återbetala enligt regler",
                      })}{" "}
                      {tierResult && (
                        <span className="font-medium">
                          ({perRulesRefundKr} kr)
                        </span>
                      )}
                    </span>
                    {tierResult && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {orgTiers.length === 0
                          ? t("refundCancel.perRulesNoTiers", {
                              defaultValue:
                                "Inga regler konfigurerade — full återbetalning ges.",
                            })
                          : `${tierResult.tierLabel} · ${Math.round(tierResult.hoursLeft)}h innan start`}
                      </div>
                    )}
                  </div>
                </label>

                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <RadioGroupItem value="cancel_no_refund" className="mt-0.5" />
                  <span>
                    {t("refundCancel.optionNoRefund", {
                      defaultValue: "Avboka utan återbetalning",
                    })}
                  </span>
                </label>
              </RadioGroup>
            </div>
          ) : (
            <div className="rounded-lg border p-3 text-sm text-muted-foreground">
              {t("refundCancel.noPaidPayments", {
                defaultValue:
                  "Bokningen har inga betalningar att återbetala.",
              })}
            </div>
          )}

          {/* Reason input */}
          <div className="space-y-1.5">
            <Label className="text-xs">
              {t("refundCancel.reasonLabel", {
                defaultValue: "Anledning (visas i loggen)",
              })}
            </Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("refundCancel.reasonPlaceholder", {
                defaultValue: 'Ex: "Kund ringde och avbokade"',
              })}
              className="h-8 text-sm"
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={cancelMutation.isPending}>
            {t("common.cancel", { defaultValue: "Avbryt" })}
          </AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {t("refundCancel.processing", { defaultValue: "Bearbetar..." })}
              </>
            ) : (
              t("refundCancel.confirm", {
                defaultValue: "Ja, avboka",
              })
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
